import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Round 7 Workstream F - the ONE frontend half of the TEST-data visibility
 * policy. Employee operational views exclude TEST records by default (the
 * backend enforces it - see backend/test_visibility.py); an authorised
 * administrator (admin.config) can opt in to see them.
 *
 * The state lives in the URL (`?include_test=true`), never in localStorage:
 * it is visible in the address bar and on the page (IncludeTestToggle shows a
 * clear "TEST records included" badge), it survives a refresh / Back like any
 * other filter, and it never silently follows an admin to another page or
 * session. For anyone who is not an authorised admin it is always false, even
 * if the URL says otherwise (the server ignores it for them too).
 *
 * Returns { includeTest, canInclude, setIncludeTest }.
 */
export function useIncludeTest() {
  const { hasCap } = useAuth();
  const [params, setParams] = useSearchParams();
  const canInclude = !!hasCap("admin.config");
  const includeTest = canInclude && params.get("include_test") === "true";
  const setIncludeTest = useCallback((on) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (on) next.set("include_test", "true");
      else next.delete("include_test");
      return next;
    }, { replace: true });
  }, [setParams]);
  return { includeTest, canInclude, setIncludeTest };
}

/** Appends `include_test=true` to a request path only when TEST is included,
 *  so the default request is byte-for-byte what it always was. */
export function withIncludeTest(path, includeTest) {
  if (!includeTest) return path;
  return `${path}${path.includes("?") ? "&" : "?"}include_test=true`;
}
